import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { ActionRedoIcon, EditIcon } from "@dotnet/telematics-design-system";

import { useDialog } from "components/Actions/ActionBar/hooks";
import { USER_ACTIONS } from "components/Actions/types";
import ParamsContext from "context/params/paramsContext";
import {
    doesAccessLevelAllowToManageUser,
    GetDisabledTextOptions,
    RenderConditionOptions,
    usePermissions,
    USER_STATUSES
} from "shared";

import { TITLES } from "../constants";

const useUserInlineActions = () => {
    const { canInviteUsers } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { PERMISSIONS, getNoPermissionTooltipTitle } = usePermissions();
    const { isOpenDialog, openDialog, closeDialog } = useDialog();

    const [rowData, setRowData] = useState<any>(null);

    const openModal = (data: any, action: USER_ACTIONS) => {
        setRowData(data);
        openDialog(action);
    };

    const closeModal = (action: USER_ACTIONS) => {
        setRowData(null);
        closeDialog(action);
    };

    const isUserHimself = (id: number, loggedUserId: number) =>
        id === loggedUserId;

    const editTooltipTitle = ({ data, user }: GetDisabledTextOptions) => {
        if (!doesAccessLevelAllowToManageUser(user!, data?.access_level)) {
            return t("Table##user cannot be edited");
        }

        return getNoPermissionTooltipTitle([
            PERMISSIONS.UserUpdate,
            PERMISSIONS.RoleRead
        ]);
    };

    const resendInvitationTooltipTitle = ({
        data,
        user
    }: GetDisabledTextOptions) => {
        if (!doesAccessLevelAllowToManageUser(user!, data?.access_level)) {
            return t("Table##user invitation cannot be resent");
        }

        return getNoPermissionTooltipTitle(PERMISSIONS.UserCreate);
    };

    const inlineActions = [
        {
            title: TITLES.EditUser,
            icon: EditIcon,
            getDisabledText: ({ data, user }: GetDisabledTextOptions) =>
                editTooltipTitle({ data, user }),
            clickAction: (data: any) => openModal(data, USER_ACTIONS.EditUser),
            renderConditions: ({ data, user }: RenderConditionOptions) => {
                const status = data?.status;

                const isNotInvitation =
                    status !== USER_STATUSES.InvitationPending &&
                    status !== USER_STATUSES.InvitationExpired;

                return isNotInvitation && !isUserHimself(user!.id, data?.id);
            }
        },
        {
            title: TITLES.ResendInvite,
            icon: ActionRedoIcon,
            getDisabledText: ({ data, user }: GetDisabledTextOptions) =>
                resendInvitationTooltipTitle({ data, user }),
            clickAction: (data: any) =>
                openModal(data, USER_ACTIONS.ResendInvitation),
            renderConditions: ({ data }: RenderConditionOptions) => {
                const status = data?.status;

                return (
                    canInviteUsers &&
                    (status === USER_STATUSES.InvitationPending ||
                        status === USER_STATUSES.InvitationExpired)
                );
            }
        }
    ];

    return {
        userInlineActions: inlineActions,
        userRowData: rowData,
        isUserEditOpen: isOpenDialog[USER_ACTIONS.EditUser],
        isInvitationResendOpen: isOpenDialog[USER_ACTIONS.ResendInvitation],
        closeDialog: closeModal
    };
};

export { useUserInlineActions };
